import {
  failure,
  weightMetrics,
} from '../domain/progress.ts';

import {
  validLocalDate,
} from '../domain/training.ts';

import type {
  ProgressResult,
} from '../domain/progress.ts';

import type {
  ProgressRepository,
} from '../ports/progress-repository.ts';

import type {
  HomeProgressSummary,
  ProgressHomeSource,
} from '../ports/home-sources.ts';

function sameContext(
  a: {
    datasetId: string;
    controlEpoch: number;
  },
  b: {
    datasetId: string;
    controlEpoch: number;
  },
) {
  return (
    a.datasetId === b.datasetId &&
    a.controlEpoch === b.controlEpoch
  );
}

export class PublicProgressHome
implements ProgressHomeSource {
  constructor(
    private readonly repository: Pick<
      ProgressRepository,
      'readContext' | 'listWeights'
    >,
  ) {}

  async readProgressHome(
    localDate: string,
  ): Promise<
    ProgressResult<HomeProgressSummary>
  > {
    if (
      !validLocalDate(localDate) ||
      localDate < '0100-01-14'
    ) {
      return failure(
        'VALIDATION_ERROR',
      );
    }

    const before =
      await this.repository.readContext();

    if (!before.ok) {
      return before;
    }

    /*
     * C-PH is reconstructed from the Progress owner.
     * Home persists none of these values.
     */
    const weights =
      await this.repository.listWeights({
        startLocalDate:
          '0100-01-01',

        endLocalDate:
          localDate,
      });

    if (!weights.ok) {
      return weights;
    }

    if (
      weights.value.some(
        view =>
          !sameContext(
            view.context,
            before.value,
          ),
      )
    ) {
      return failure(
        'DATASET_STALE',
      );
    }

    const trend =
      weightMetrics(
        weights.value.map(
          view =>
            view.observation,
        ),
        localDate,
      );

    if (!trend.ok) {
      return trend;
    }

    const after =
      await this.repository.readContext();

    if (
      !after.ok ||
      !sameContext(
        before.value,
        after.value,
      )
    ) {
      return after.ok
        ? failure(
            'DATASET_STALE',
          )
        : after;
    }

    const latest =
      weights.value[0]
        ?.observation;

    return {
      ok: true,
      value: {
        localDate,

        ...(latest
          ? {
              latestWeight:
                latest,
            }
          : {}),

        recent: {
          startLocalDate:
            trend.value.recent
              .startLocalDate,

          endLocalDate:
            trend.value.recent
              .endLocalDate,

          count:
            trend.value.recent.count,

          coverage:
            trend.value.recent.coverage,

          ...(trend.value.recent
            .display !== undefined
            ? {
                display:
                  trend.value.recent
                    .display,
              }
            : {}),
        },

        previous: {
          startLocalDate:
            trend.value.previous
              .startLocalDate,

          endLocalDate:
            trend.value.previous
              .endLocalDate,

          count:
            trend.value.previous.count,

          coverage:
            trend.value.previous
              .coverage,
        },

        ...(trend.value
          .displayDifference !==
        undefined
          ? {
              displayDifference:
                trend.value
                  .displayDifference,
            }
          : {}),

        context: { ...before.value },
      },
    };
  }
}